/**
 * Locale-aware formatting helpers bound to the ACTIVE app language (not the
 * browser locale) — screens adopt these in later phases to replace hardcoded
 * 'en-US' and browser-locale call sites.
 *
 * Country display names use Intl.DisplayNames with the existing English
 * catalog as the deterministic fallback (audit §8): stored/transmitted codes
 * never change; only display is localized.
 */
import { countryNameFor, normalizeCountryCode } from '../config/countryCatalog';
import { getCurrentLocale } from './index';
import type { AppLocale } from './locale';

export function formatDate(
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(getCurrentLocale(), options).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  try {
    return new Intl.NumberFormat(getCurrentLocale(), options).format(value);
  } catch {
    return String(value);
  }
}

export function formatRelative(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  options?: Intl.RelativeTimeFormatOptions,
): string {
  try {
    return new Intl.RelativeTimeFormat(getCurrentLocale(), {
      numeric: 'auto',
      ...options,
    }).format(value, unit);
  } catch {
    return `${value} ${unit}`;
  }
}

// One Intl.DisplayNames per locale; null marks "unavailable, use fallback".
const displayNamesCache = new Map<AppLocale, Intl.DisplayNames | null>();

function regionDisplayNames(locale: AppLocale): Intl.DisplayNames | null {
  if (!displayNamesCache.has(locale)) {
    let instance: Intl.DisplayNames | null = null;
    try {
      if (typeof Intl.DisplayNames === 'function') {
        // fallback: 'none' → of() returns undefined instead of echoing the
        // code, so "no useful label" is detectable below.
        instance = new Intl.DisplayNames(locale, { type: 'region', fallback: 'none' });
      }
    } catch {
      instance = null;
    }
    displayNamesCache.set(locale, instance);
  }
  return displayNamesCache.get(locale) ?? null;
}

/**
 * Localized country name for a stored ISO code, in the active app language.
 * Falls back to the English catalog name whenever Intl.DisplayNames is
 * unavailable, throws, or yields no useful label; returns null only for
 * unsupported/unset codes (mirroring countryNameFor).
 */
export function localizedCountryName(raw: unknown): string | null {
  const code = normalizeCountryCode(raw);
  if (!code) return null;
  const english = countryNameFor(code);
  try {
    const names = regionDisplayNames(getCurrentLocale());
    if (!names) return english;
    const label = names.of(code.toUpperCase());
    if (!label || label === code.toUpperCase()) return english;
    return label;
  } catch {
    return english;
  }
}

/** Test hook: clear memoized Intl instances after stubbing globals. */
export function __resetFormatCachesForTests(): void {
  displayNamesCache.clear();
}
